import { t } from "@hakili/i18n";
import { Verdict } from "@hakili/ui";
import { AlertTriangle, Check, FileText, Lock } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { type AnalysisPointView, verdictLabel } from "./analyse.js";
import { fill } from "./dossiers.js";
import { InertAct } from "./inert-act.js";
import { calculationSheetOf, sheetNotComputedLine } from "./quotite-sheet.js";

/**
 * `ARC-1` (D-1026-2, the founder's R1: **the revue is D2's TABLE**), D2 frames 20 to 25 (`analyst.js` `pointRow`, `pointDetail`,
 * `components.css` `.pt`): five columns , « # », « Point », « Hakili propose », « Preuve », « Votre acte » , one row per point,
 * under the three sub-steps. A row is the point's label with Hakili's reason under it, Hakili's chip, the evidence's own place
 * (« p. 2 », « calcul »), and the analyst's act: « Examiner » opens the row, a settled point reads « Confirmé » / « Corrigé » /
 * « Signalé » with « Reprendre ». The open row carries the calculation sheet or « Voir la pièce », the criblage form on P14, and the
 * buttons. At or under 1 099 px the table keeps three columns, the act drops under the label, and « Preuve » is not drawn (D2).
 *
 * What D2 does not draw is not drawn: the proposal's engine line, the evidence sentence and who-and-when of an act are gone from
 * the row (the table's chip and the act word say them); the criblage form (the founder's keep) and the policy re-run's line (a
 * state the file is in, awaiting her act) stay.
 */
export const COLUMNS =
  "grid grid-cols-[44px_minmax(0,1.6fr)_140px_88px_150px] max-[1099px]:grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-3.5 px-5";

/** D2's micro-label: 12 px capitals, one line (the instrument lets a short upper-case label stand below 14 px). */
const HEAD =
  "whitespace-nowrap text-xs font-semibold uppercase tracking-[.08em] text-[color:var(--hk-text-2)]";

const BTN =
  "inline-flex min-h-[34px] items-center justify-center rounded-[10px] border border-[color:var(--hk-line)] bg-white px-3 text-sm font-semibold text-[color:var(--hk-text)] hover:bg-[color:var(--hk-surface-2)] disabled:opacity-60";
const BTN_DARK =
  "inline-flex min-h-[40px] items-center justify-center rounded-[10px] border border-[color:var(--hk-text)] bg-[color:var(--hk-text)] px-4 text-sm font-semibold text-white disabled:opacity-60";
const BTN_PALE =
  "inline-flex min-h-[40px] cursor-not-allowed items-center justify-center rounded-[10px] bg-[color:var(--hk-brand-tint)] px-4 text-sm font-semibold text-[color:var(--hk-brand-strong)]";
const BTN_QUIET =
  "inline-flex min-h-[34px] items-center justify-center rounded-[10px] px-3 text-sm font-semibold text-[color:var(--hk-brand-strong)] hover:bg-[color:var(--hk-brand-wash)]";

export function TableHead(): ReactElement {
  return (
    <div
      className={`${COLUMNS} h-[38px] border-b border-[color:var(--hk-line-soft)] bg-[color:var(--hk-surface-2)]`}
    >
      <span data-meta className={HEAD}>
        {t("bank.revue.colNumero")}
      </span>
      <span data-meta className={HEAD}>
        {t("bank.revue.colPoint")}
      </span>
      <span data-meta className={HEAD}>
        {t("bank.revue.colPropose")}
      </span>
      <span data-meta className={`${HEAD} max-[1099px]:hidden`}>
        {t("bank.revue.colPreuve")}
      </span>
      <span data-meta className={`${HEAD} text-right max-[1099px]:hidden`}>
        {t("bank.revue.colActe")}
      </span>
    </div>
  );
}

/** The evidence's own place, from the door's fields: the page where the proposal read one, « calcul » on a figures point. */
export function evidencePlace(proposal: AnalysisPointView): string {
  if (proposal.evidence?.kind === "document") {
    return fill("bank.revue.preuvePage", { n: String(proposal.evidence.page) });
  }
  return proposal.evidence?.kind === "figures" ? t("bank.revue.preuveCalcul") : t("bank.revue.preuvePieces");
}

export interface RowProps {
  readonly number: number;
  readonly code: string;
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly flagged: boolean;
  readonly flagNote: string | null;
  readonly retiredNote: string | null;
  readonly proposal: AnalysisPointView | null;
  readonly open: boolean;
  readonly busy: boolean;
  /** The reason her verdict waits (P14's screening), or `null`; said once, at the form (`InertAct` points at it). */
  readonly guard: string | null;
  readonly guardId: string | null;
  /** The criblage form, on P14 only. */
  readonly screening: ReactNode;
  /** The pièce viewer opens from this point: stands only where the shell handed the door. */
  readonly canView: boolean;
  readonly noteOpen: boolean;
  readonly noteText: string;
  readonly onToggle: () => void;
  readonly onView: () => void;
  readonly onConfirm: () => void;
  readonly onConform: () => void;
  readonly onFlagOpen: () => void;
  readonly onReopen: () => void;
  readonly onNoteText: (text: string) => void;
  readonly onNoteSave: () => void;
  /** `ARC2-SHEET` (D2 frame 35): « Voir la politique » on the sheet's null card, for a reader who has that screen. */
  readonly onPolitique?: () => void;
}

export function PointRow(props: RowProps): ReactElement {
  const { proposal } = props;
  const settled = props.checked || props.flagged;
  const state = props.flagged ? "signale" : props.checked ? "conforme" : "ouvert";
  const corrected = proposal?.act?.act === "corrigee";
  const calc = proposal === null ? null : calculationSheetOf(proposal, props.onPolitique);
  const evidence = proposal === null ? null : evidencePlace(proposal);
  const viewable = proposal?.evidence?.kind === "document" && props.canView;
  const num = String(props.number).padStart(2, "0");
  const confirmable = proposal !== null && proposal.verdict === "conforme" && calc === null;
  const notComputedLine = proposal === null ? null : sheetNotComputedLine(proposal);
  const chip =
    proposal === null || notComputedLine !== null
      ? { kind: "none" as const, label: t("bank.revue.sansProposition") }
      : { kind: proposal.verdict, label: verdictLabel(proposal.verdict) };
  return (
    <li
      className={
        `${COLUMNS} relative border-b border-[color:var(--hk-line-soft)] py-3.5 ` +
        (props.flagged
          ? "bg-amber-50 shadow-[inset_3px_0_0_var(--hk-warn)]"
          : props.open && !settled
            ? "bg-[color:var(--hk-surface-2)] shadow-[inset_3px_0_0_var(--hk-brand)]"
            : "")
      }
      data-testid={`point-${props.code}`}
      data-state={state}
    >
      <span
        data-meta
        className={
          "font-[family-name:var(--hk-mono,ui-monospace)] text-sm " +
          (props.open && !settled ? "text-[color:var(--hk-brand-strong)]" : "text-[color:var(--hk-text-2)]")
        }
      >
        {num}
      </span>
      <span className="min-w-0">
        <b
          className="block text-[15px] font-semibold text-[color:var(--hk-text)]"
          data-testid={`point-${props.code}-titre`}
        >
          {props.label}
        </b>
        <em
          className="mt-0.5 block text-sm not-italic text-[color:var(--hk-text-2)]"
          data-testid={`point-${props.code}-hakili-motif`}
        >
          {proposal === null ? t("bank.revue.pasDeProposition") : (notComputedLine ?? proposal.reason)}
        </em>
        {props.retiredNote === null ? null : (
          <em
            className="mt-0.5 block text-sm not-italic text-[color:var(--hk-text-2)]"
            data-testid={`point-${props.code}-retire`}
          >
            {props.retiredNote}
          </em>
        )}
      </span>
      <span>
        <span data-testid={`point-${props.code}-hakili-verdict`} data-verdict={chip.kind}>
          <Verdict kind={chip.kind} label={chip.label} className="whitespace-nowrap" />
        </span>
      </span>
      <span className="max-[1099px]:hidden">
        {evidence === null ? null : viewable ? (
          <button
            type="button"
            onClick={props.onView}
            aria-label={fill("bank.revue.voirPieceDuPoint", { n: String(props.number) })}
            className="rounded-md bg-[color:var(--hk-brand-wash)] px-[9px] py-1 font-[family-name:var(--hk-mono,ui-monospace)] text-[13px] font-medium text-[color:var(--hk-brand-strong)]"
            data-testid={`point-${props.code}-ouvrir`}
          >
            {evidence}
          </button>
        ) : (
          <span
            data-meta
            className="rounded-md bg-[color:var(--hk-brand-wash)] px-[9px] py-1 font-[family-name:var(--hk-mono,ui-monospace)] text-[13px] font-medium text-[color:var(--hk-brand-strong)]"
            data-testid={`point-${props.code}-preuve`}
          >
            {evidence}
          </span>
        )}
      </span>
      <span className="flex items-center justify-end gap-1.5 max-[1099px]:col-[2/-1] max-[1099px]:justify-start">
        {props.flagged ? (
          <>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--hk-warn)]">
              <AlertTriangle className="h-[15px] w-[15px]" aria-hidden="true" />
              {t("bank.revue.etatSignale")}
            </span>
            <button type="button" disabled={props.busy} onClick={props.onReopen} className={BTN_QUIET}>
              {t("bank.revue.reprendre")}
            </button>
          </>
        ) : props.checked ? (
          <>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--hk-ok)]">
              <Check className="h-4 w-4" aria-hidden="true" />
              {corrected ? t("bank.revue.corrige") : t("bank.revue.confirme")}
            </span>
            <button type="button" disabled={props.busy} onClick={props.onReopen} className={BTN_QUIET}>
              {t("bank.revue.reprendre")}
            </button>
          </>
        ) : props.open ? null : (
          <button
            type="button"
            onClick={props.onToggle}
            aria-label={fill("bank.revue.examinerLePoint", { n: String(props.number) })}
            className={BTN}
            data-testid={`point-${props.code}-examiner`}
          >
            {t("bank.revue.examiner")}
          </button>
        )}
      </span>
      {props.flagged && props.flagNote !== null ? (
        <p
          className="col-[1/-1] -mt-1 flex items-center gap-2 pl-[58px] text-sm text-[color:var(--hk-warn)]"
          data-testid={`point-${props.code}-note`}
        >
          <AlertTriangle className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
          {fill("bank.revue.votreNote", { note: props.flagNote })}
        </p>
      ) : null}
      {props.open && !settled ? (
        <div
          className="col-[1/-1] pb-1 pl-[58px] pt-3.5 max-[1099px]:pl-0"
          data-testid={`point-${props.code}-detail`}
        >
          {calc !== null ? (
            <div data-testid={`point-${props.code}-hakili-calcul`}>{calc}</div>
          ) : proposal !== null && viewable ? (
            <div className="mb-1.5">
              <button
                type="button"
                onClick={props.onView}
                className={BTN_QUIET}
                data-testid={`point-${props.code}-voir`}
              >
                <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("bank.revue.voirPiece")}
              </button>
            </div>
          ) : null}
          {props.screening}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {props.guard !== null && props.guardId !== null ? (
              <>
                <InertAct
                  describedBy={props.guardId}
                  testId={`point-${props.code}-cocher`}
                  className={BTN_PALE}
                >
                  {confirmable ? t("bank.revue.confirmerProposition") : t("bank.revue.conforme")}
                </InertAct>
                <InertAct describedBy={props.guardId} testId={`point-${props.code}-signaler`} className={BTN}>
                  {t("bank.revue.signaler")}
                </InertAct>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={props.busy}
                  onClick={confirmable ? props.onConfirm : props.onConform}
                  className={BTN_DARK}
                  data-testid={`point-${props.code}-cocher`}
                >
                  {confirmable ? t("bank.revue.confirmerProposition") : t("bank.revue.conforme")}
                </button>
                <button
                  type="button"
                  disabled={props.busy}
                  onClick={props.onFlagOpen}
                  className={BTN}
                  data-testid={`point-${props.code}-signaler`}
                >
                  {t("bank.revue.signaler")}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={props.onToggle}
              className={BTN_QUIET}
              data-testid={`point-${props.code}-fermer`}
            >
              {t("bank.revue.fermer")}
            </button>
          </div>
          {props.guard !== null && props.guardId !== null ? (
            <p
              id={props.guardId}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[color:var(--hk-warn-bg)] px-3 py-1.5 text-sm text-[color:var(--hk-warn)]"
              data-testid={props.guardId}
            >
              <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
              {props.guard}
            </p>
          ) : null}
          {props.noteOpen ? (
            <div className="mt-3 grid gap-2">
              <label
                className="text-sm font-semibold text-[color:var(--hk-text)]"
                htmlFor={`note-${props.id}`}
              >
                {t("bank.revue.noteAide")}
              </label>
              <textarea
                id={`note-${props.id}`}
                value={props.noteText}
                onChange={(event) => {
                  props.onNoteText(event.target.value);
                }}
                rows={3}
                className="w-full max-w-xl rounded-[10px] border border-[color:var(--hk-line-strong)] bg-white px-3 py-2 text-sm text-[color:var(--hk-text)]"
                data-testid={`point-${props.code}-note-saisie`}
              />
              <div>
                <button
                  type="button"
                  disabled={props.noteText.trim() === "" || props.busy}
                  onClick={props.onNoteSave}
                  className={BTN_DARK}
                  data-testid={`point-${props.code}-note-enregistrer`}
                >
                  {t("bank.revue.noteEnregistrer")}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
